import { useCallback, useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"

import { logger } from "@/lib/logger"

const log = logger("UnsavedChanges")
const ROUTE_CHANGE_ABORT_MESSAGE = "Route change aborted by user"
const routeChangeAbortError = Object.assign(
  new Error(ROUTE_CHANGE_ABORT_MESSAGE),
  { cancelled: true as const },
)

// Next's Pages Router requires a synchronous throw to cancel routeChangeStart.
// Keeping the intentional cancellation stackless prevents the dev overlay from
// presenting it as an application crash.
routeChangeAbortError.stack = undefined
const CONFIRMED_NAVIGATION_RESET_DELAY_MS = 1000

function getNavigationPath(url: string): string {
  return url.split(/[?#]/, 1)[0] ?? url
}

function isRouteCancellationError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "cancelled" in error &&
    error.cancelled === true
  )
}

interface UseUnsavedChangesOptions {
  hasUnsavedChanges: () => boolean
  isNavigationLocked?: boolean
  onSave?: () => Promise<void> | void
  onDiscard?: () => void
  customNavigation?: () => void
  validateBeforeSave?: () => boolean
  isSameNavigationContext?: (nextUrl: string, currentUrl: string) => boolean
}

interface UseUnsavedChangesReturn {
  isModalOpen: boolean
  isSaving: boolean
  pendingUrl: string | null
  error: string | null
  handleConfirmSave: () => Promise<void>
  handleConfirmDiscard: () => void
  handleConfirmCancel: () => void
  handleNavigationAttempt: () => void
}

export const useUnsavedChanges = ({
  hasUnsavedChanges,
  isNavigationLocked = false,
  onSave,
  onDiscard,
  customNavigation,
  validateBeforeSave,
  isSameNavigationContext,
}: UseUnsavedChangesOptions): UseUnsavedChangesReturn => {
  const router = useRouter()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const navigationBlockedRef = useRef(false)
  const mountedRef = useRef(true)
  const navigationGenerationRef = useRef(0)
  const saveOperationRef = useRef(0)
  const confirmedNavigationRef = useRef(false)
  const confirmedNavigationResetTimeoutRef = useRef<number | undefined>(
    undefined,
  )
  const currentUrlRef = useRef(router.asPath)
  currentUrlRef.current = router.asPath

  const clearConfirmedNavigation = useCallback(() => {
    confirmedNavigationRef.current = false

    if (confirmedNavigationResetTimeoutRef.current) {
      window.clearTimeout(confirmedNavigationResetTimeoutRef.current)
      confirmedNavigationResetTimeoutRef.current = undefined
    }
  }, [])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault()
        e.returnValue = ""
      }
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (
        event.reason === routeChangeAbortError ||
        isRouteCancellationError(event.reason)
      ) {
        event.preventDefault()
      }
    }

    const handleRouteChange = (url: string) => {
      if (confirmedNavigationRef.current) {
        return
      }

      // Query-only navigation stays on the same mounted page. In particular,
      // organization detail tabs preserve the force-mounted Configure form, so
      // changing tabs cannot discard its draft.
      const samePath =
        getNavigationPath(url) === getNavigationPath(currentUrlRef.current)
      if (
        isSameNavigationContext?.(url, currentUrlRef.current) ||
        (!isSameNavigationContext && samePath)
      ) {
        return
      }

      if (isNavigationLocked) {
        navigationBlockedRef.current = true
        navigationGenerationRef.current += 1
        router.events.emit("routeChangeError", routeChangeAbortError, url, {
          shallow: false,
        })
        throw routeChangeAbortError
      }

      if (hasUnsavedChanges()) {
        navigationBlockedRef.current = true
        navigationGenerationRef.current += 1
        setPendingUrl(url)
        setError(null)
        setIsModalOpen(true)
        router.events.emit("routeChangeError", routeChangeAbortError, url, {
          shallow: false,
        })
        throw routeChangeAbortError
      }
    }

    const handleRouteChangeError = (_err: unknown, _url: string): void => {
      clearConfirmedNavigation()

      if (navigationBlockedRef.current) {
        navigationBlockedRef.current = false
      }
    }

    const handleRouteChangeComplete = (): void => {
      clearConfirmedNavigation()
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    window.addEventListener("unhandledrejection", handleUnhandledRejection)
    router.events.on("routeChangeStart", handleRouteChange)
    router.events.on("routeChangeError", handleRouteChangeError)
    router.events.on("routeChangeComplete", handleRouteChangeComplete)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      window.removeEventListener("unhandledrejection", handleUnhandledRejection)
      router.events.off("routeChangeStart", handleRouteChange)
      router.events.off("routeChangeError", handleRouteChangeError)
      router.events.off("routeChangeComplete", handleRouteChangeComplete)
    }
  }, [
    clearConfirmedNavigation,
    hasUnsavedChanges,
    isNavigationLocked,
    router.events,
    isSameNavigationContext,
  ])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      navigationGenerationRef.current += 1
      saveOperationRef.current += 1
      clearConfirmedNavigation()
    }
  }, [clearConfirmedNavigation])

  const continueConfirmedNavigation = useCallback(() => {
    clearConfirmedNavigation()
    confirmedNavigationRef.current = true
    confirmedNavigationResetTimeoutRef.current = window.setTimeout(
      clearConfirmedNavigation,
      CONFIRMED_NAVIGATION_RESET_DELAY_MS,
    )

    try {
      if (pendingUrl && pendingUrl !== "custom") {
        void router.push(pendingUrl)
      } else if (customNavigation) {
        customNavigation()
      } else {
        router.back()
      }
    } finally {
      setPendingUrl(null)
    }
  }, [clearConfirmedNavigation, pendingUrl, customNavigation, router])

  const handleConfirmSave = useCallback(async (): Promise<void> => {
    if (!onSave) {
      return
    }

    // Check validation before attempting to save
    if (validateBeforeSave && !validateBeforeSave()) {
      // Validation failed - close modal so user can see validation errors
      setIsModalOpen(false)
      navigationBlockedRef.current = false
      return
    }

    const navigationGeneration = navigationGenerationRef.current
    const saveOperation = saveOperationRef.current + 1
    saveOperationRef.current = saveOperation
    setIsSaving(true)
    setError(null)
    try {
      await onSave()
      if (
        !mountedRef.current ||
        navigationGeneration !== navigationGenerationRef.current ||
        saveOperation !== saveOperationRef.current
      ) {
        return
      }
      setIsModalOpen(false)
      navigationBlockedRef.current = false
      continueConfirmedNavigation()
    } catch (error) {
      if (
        !mountedRef.current ||
        navigationGeneration !== navigationGenerationRef.current ||
        saveOperation !== saveOperationRef.current
      ) {
        return
      }
      log.error("Save failed", error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to save changes. Please try again."
      setError(errorMessage)
    } finally {
      if (mountedRef.current && saveOperation === saveOperationRef.current) {
        setIsSaving(false)
      }
    }
  }, [onSave, validateBeforeSave, continueConfirmedNavigation])

  const handleConfirmDiscard = useCallback(() => {
    if (onDiscard) {
      onDiscard()
    }
    setIsModalOpen(false)
    navigationGenerationRef.current += 1
    saveOperationRef.current += 1
    setIsSaving(false)
    navigationBlockedRef.current = false
    continueConfirmedNavigation()
  }, [onDiscard, continueConfirmedNavigation])

  const handleConfirmCancel = useCallback(() => {
    setIsModalOpen(false)
    navigationGenerationRef.current += 1
    saveOperationRef.current += 1
    setIsSaving(false)
    setPendingUrl(null)
    setError(null)
    navigationBlockedRef.current = false
  }, [])

  const handleNavigationAttempt = useCallback(() => {
    if (isNavigationLocked) {
      return
    }

    if (hasUnsavedChanges()) {
      navigationGenerationRef.current += 1
      setPendingUrl("custom")
      setError(null)
      setIsModalOpen(true)
    } else if (customNavigation) {
      customNavigation()
    } else {
      router.back()
    }
  }, [hasUnsavedChanges, isNavigationLocked, customNavigation, router])

  return {
    isModalOpen,
    isSaving,
    pendingUrl,
    error,
    handleConfirmSave,
    handleConfirmDiscard,
    handleConfirmCancel,
    handleNavigationAttempt,
  }
}
